'use client'

import * as React from 'react'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { Calendar, CalendarProps } from '@/components/ui/calendar'
import { fieldIconClassName, fieldVariants } from '@/components/ui/field-variants'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { formatLocalizedDate } from '@/lib/utils/date-locale'

type DatePickerProps = Omit<CalendarProps, 'mode' | 'captionLayout' | 'selected'> & {
  selected?: Date | undefined
  onSelect?: (date: Date) => void
  calendarClassName?: string
  /** Disables the trigger button (`disabled` is the calendar's day matcher). */
  triggerDisabled?: boolean
  /** Field size (fieldVariants): `xs` in widget toolbars, larger in forms and dialogs. */
  size?: 'xs' | 'sm' | 'default'
  /** What an empty field reads ("Select date" by default, or the value it stands for). */
  emptyLabel?: string
  /** The date an empty field stands for: the calendar opens on it and marks it. */
  placeholderDate?: Date
  /** Earliest / latest pickable day — also bounds the month/year dropdowns. */
  minDate?: Date
  maxDate?: Date
  /** Shows a reset button under the calendar while a date is set. */
  onClear?: () => void
  clearLabel?: string
  /** Date and time: a time field under the calendar; picking a day keeps the time. */
  withTime?: boolean
  /** Portal target for the popover (e.g. inside a menu that would treat it as outside). */
  container?: Element | null
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * The app's one date field (the data widget's look): a field button that opens a calendar whose
 * caption jumps by month/year, so an old date is two picks rather than dozens of ‹ clicks.
 */
export function DatePicker({
  selected,
  onSelect,
  className,
  calendarClassName,
  triggerDisabled,
  size = 'xs',
  emptyLabel,
  placeholderDate,
  minDate,
  maxDate,
  onClear,
  clearLabel,
  withTime = false,
  container,
  disabled,
  ...props
}: DatePickerProps) {
  const locale = useLocale()
  const tCommon = useTranslations('common')
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(selected)

  React.useEffect(() => {
    setDate(selected)
  }, [selected])

  const shown = date ?? placeholderDate
  const label = date
    ? formatLocalizedDate(date, locale, 'shortDateWithYear') +
      (withTime ? ` ${pad2(date.getHours())}:${pad2(date.getMinutes())}` : '')
    : (emptyLabel ?? 'Select date')

  const bounds = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ]
  const dayDisabled = disabled
    ? [...(Array.isArray(disabled) ? disabled : [disabled]), ...bounds]
    : bounds.length
      ? bounds
      : undefined

  const commit = (next: Date) => {
    setDate(next)
    onSelect?.(next)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={triggerDisabled}
          className={cn(
            fieldVariants({ size }),
            'group w-auto min-w-24 gap-1.5 rounded-sm whitespace-nowrap',
            // Muted only when empty means "nothing": an empty field standing for a date
            // (placeholderDate, e.g. the data widget's "7 days ago") reads as that value.
            !date && !placeholderDate && 'text-foreground/60',
            className
          )}
        >
          <span className="min-w-0 truncate">{label}</span>
          <ChevronDownIcon className={fieldIconClassName} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        container={container}
        // Above menus (dropdown z-60, FilterRow submenus z-200): a date field inside a filter
        // submenu must open its calendar on top of that submenu, not under it.
        className="no-drag z-300 w-auto overflow-hidden p-0"
        align="start"
      >
        <Calendar
          mode="single"
          selected={shown}
          defaultMonth={shown ?? maxDate}
          startMonth={minDate}
          endMonth={maxDate}
          captionLayout="dropdown"
          className={calendarClassName}
          disabled={dayDisabled}
          onSelect={(day) => {
            if (!day) return
            if (withTime) {
              // A day pick keeps the time already chosen (midnight for a fresh value).
              const next = new Date(day)
              next.setHours(date?.getHours() ?? 0, date?.getMinutes() ?? 0, 0, 0)
              commit(next)
              return
            }
            commit(day)
            setOpen(false)
          }}
          {...props}
        />
        {(withTime || (date && onClear)) && (
          <div className="border-foreground/10 flex items-center gap-2 border-t px-3 py-2.5">
            {withTime && (
              <input
                type="time"
                aria-label={tCommon('time')}
                value={date ? `${pad2(date.getHours())}:${pad2(date.getMinutes())}` : ''}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number)
                  if (!Number.isFinite(h) || !Number.isFinite(m)) return
                  const next = new Date(date ?? shown ?? new Date())
                  next.setHours(h, m, 0, 0)
                  commit(next)
                }}
                className={cn(fieldVariants({ size: 'xs' }), 'w-auto flex-1')}
              />
            )}
            {date && onClear && (
              <Button
                type="button"
                variant="outline"
                size="xs"
                className={withTime ? undefined : 'w-full'}
                onClick={() => {
                  onClear()
                  setDate(undefined)
                  setOpen(false)
                }}
              >
                {clearLabel ?? tCommon('clear')}
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

type MonthPickerProps = {
  selected?: { year: number; month: number } | undefined
  onSelect?: (value: { year: number; month: number }) => void
  minYear?: number
  maxYear?: number
}

export function MonthPicker({
  selected,
  onSelect,
  minYear = 2020,
  maxYear = new Date().getFullYear() + 5,
}: MonthPickerProps) {
  const tCommon = useTranslations('common')
  const [open, setOpen] = React.useState(false)
  const [viewYear, setViewYear] = React.useState(selected?.year ?? new Date().getFullYear())

  React.useEffect(() => {
    if (selected?.year) {
      setViewYear(selected.year)
    }
  }, [selected?.year])

  const handleMonthSelect = (month: number) => {
    onSelect?.({ year: viewYear, month })
    setOpen(false)
  }

  const formatDisplay = () => {
    if (!selected) return 'Select month'
    return `${selected.year}-${String(selected.month).padStart(2, '0')}`
  }

  return (
    <div className="flex flex-col gap-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              fieldVariants({ size: 'xs' }),
              'group w-auto min-w-24 rounded-sm tabular-nums'
            )}
          >
            {formatDisplay()}
            <ChevronDownIcon className={fieldIconClassName} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-2" align="start">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Button
                aria-label={tCommon('previousYear')}
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setViewYear((y) => Math.max(minYear, y - 1))}
                disabled={viewYear <= minYear}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">{viewYear}</span>
              <Button
                aria-label={tCommon('nextYear')}
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setViewYear((y) => Math.min(maxYear, y + 1))}
                disabled={viewYear >= maxYear}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-px">
              {MONTHS.map((monthName, index) => {
                const monthNumber = index + 1
                const isSelected = selected?.year === viewYear && selected?.month === monthNumber
                return (
                  <Button
                    key={monthName}
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'h-8 px-3 text-sm',
                      isSelected
                        ? 'bg-emphasis text-emphasis-foreground not-disabled:hover:bg-emphasis/90'
                        : 'text-foreground/70'
                    )}
                    onClick={() => handleMonthSelect(monthNumber)}
                  >
                    {monthName}
                  </Button>
                )
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
